'use client';

import { DateTime } from "luxon";
import React, { useRef, useState, useCallback, useMemo } from "react";

import { APIError, useAxios } from "../../core/_axios";
import { LuluShippingOption, LuluEstimatedCost, PhysicalBook } from "../../core/_models";
import { getEstimatedLuluShippingOptions, estimateLuluPrintJobCost, createLuluPrintJob } from "../../core/_requests";
import { Link } from "react-router-dom";

export function Button({
    color,
    className,
    children,
    onClick,
    type,
    disabled
}: {
    color: 'blue' | 'dark',
    className?: string,
    children: React.ReactNode,
    onClick?: () => void,
    type?: 'button' | 'submit',
    disabled?: boolean
}) {
    return <button
        type={type || 'button'}
        className={`px-4 py-2 rounded-md text-white ${color === 'blue' ? 'bg-blue-500' : 'bg-gray-500'} ${className}`}
        onClick={onClick}
        disabled={disabled}
    >
        {children}
    </button>
}

const CoverPage = React.forwardRef<HTMLDivElement, any>((props, ref) => {
    return (
        <div ref={ref}>
            <div style={{ backgroundColor: 'rgb(43, 53, 80)', height: '100%' }}>
                {props.children}
            </div>
        </div>
    );
});

CoverPage.displayName = 'CoverPage';

const InteriorPage = React.forwardRef<HTMLDivElement, any>((props, ref) => {
    return (
        <div ref={ref}>
            <div style={{ color: 'black', backgroundColor: '#f7f7f5', height: '100%' }}>
                {props.children}
            </div>
        </div>
    );
});

InteriorPage.displayName = 'InteriorPage';

const states = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AS", label: "American Samoa" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "AA", label: "Armed Forces Americas" },
    { value: "AE", label: "Armed Forces Europe" },
    { value: "AP", label: "Armed Forces Pacific" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "DC", label: "District of Columbia" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "GU", label: "Guam" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MH", label: "Marshall Islands" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "FM", label: "Micronesia" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "MP", label: "Northern Mariana Islands" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PW", label: "Palau" },
    { value: "PA", label: "Pennsylvania" },
    { value: "PR", label: "Puerto Rico" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VI", label: "Virgin Islands" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" }
];

function ShippingPreview({
    service_generated_book,
}: {
    service_generated_book: PhysicalBook,
}) {

    const axios = useAxios()

    const [loadingShippingOptions, setLoadingShippingOptions] = useState(true)
    const [errorLoadingShippingOptions, setErrorLoadingShippingOptions] = useState('')

    const [availableShippingOptions, setAvailableShippingOptions] = useState<LuluShippingOption[]>([])

    const [numberOfBooks, setNumberOfBooks] = useState<string>('3')

    const [selectedShippingOption, setSelectedShippingOption] = useState<string>('EXPRESS')

    const [estimatingCost, setEstimatingCost] = useState(false)
    const [errorMessageEstimatingCost, setErrorMessageEstimatingCost] = useState('')

    const [creatingLuluPrintJob, setCreatingLuluPrintJob] = useState(false)
    const [errorCreatingLuluPrintJob, setErrorCreatingLuluPrintJob] = useState('')
    const [successCreatingLuluPrintJob, setSuccessCreatingLuluPrintJob] = useState('')

    const [shipToName, setShipToName] = useState<string>('')
    const [addressLine1, setAddressLine1] = useState<string>('')
    const [addressLine2, setAddressLine2] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [state, setState] = useState<string>('')
    const [zipCode, setZipCode] = useState<string>('')
    const [phoneNumber, setPhoneNumber] = useState<string>('')
    const [emailAddress, setEmailAddress] = useState<string>('')

    const [estimatedCost, setEstimatedCost] = useState<LuluEstimatedCost | null>(null)

    const tryToGetShippingOptions = useCallback(async (quantityOfBooks: number, shippingAddress: { address_line_1: string, address_line_2: string, city: string, state: string, zip_code: string, phone_number: string, email_address: string }) => {
        setErrorLoadingShippingOptions('')
        setLoadingShippingOptions(true)
        try {
            const updatedOptions = await getEstimatedLuluShippingOptions(axios, {
                service_unique_identifier: service_generated_book.service.unique_identifier,
                quantity_of_books: quantityOfBooks,
                shipping_address: shippingAddress
            })
            setAvailableShippingOptions(updatedOptions)
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorLoadingShippingOptions(errorMessage)
        } finally {
            setLoadingShippingOptions(false)
        }
    }, [axios, service_generated_book.service.unique_identifier])

    const estimateCosts = useCallback(async (forceQuantity: number | null, forceShippingOption: string | null = null) => {
        if (estimatingCost) {
            return
        }
        // Make a request to estimateLuluPrintJobCost:
        setEstimatingCost(true)
        setErrorMessageEstimatingCost('')
        setEstimatedCost(null)
        try {
            const quantityOfBooks = forceQuantity ? forceQuantity : parseInt(numberOfBooks, 10)
            const shippingAddress = {
                name: shipToName || '',
                address_line_1: addressLine1 || '',
                address_line_2: addressLine2 || '',
                city: city || '',
                state: state || '',
                zip_code: zipCode || '',
                phone_number: phoneNumber || '',
                email_address: emailAddress || ''
            }
            const updatedCost = await estimateLuluPrintJobCost(axios, {
                service_unique_identifier: service_generated_book.service.unique_identifier,
                quantity_of_books: quantityOfBooks,
                shipping_option: forceShippingOption ? forceShippingOption : selectedShippingOption,
                shipping_address: shippingAddress
            })
            setEstimatedCost(updatedCost)
            setEstimatingCost(false)
            // We call tryToGetShippingOptions here after because the shipping address is now precise
            tryToGetShippingOptions(quantityOfBooks, shippingAddress)
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorMessageEstimatingCost(errorMessage)
            setEstimatingCost(false)
        }
    }, [estimatingCost, numberOfBooks, shipToName, addressLine1, addressLine2, city, state, zipCode, phoneNumber, emailAddress, axios, service_generated_book.service.unique_identifier, selectedShippingOption, tryToGetShippingOptions])

    const handleCreateLuluPrintJob = useCallback(async () => {
        if (!estimatedCost) {
            return
        }
        setErrorCreatingLuluPrintJob('')
        setSuccessCreatingLuluPrintJob('')
        setCreatingLuluPrintJob(true)
        try {
            const response = await createLuluPrintJob(axios, {
                service_generated_book_unique_identifier: service_generated_book.unique_identifier,
                quantity_of_books: parseInt(numberOfBooks, 10),
                shipping_option: selectedShippingOption,
                shipping_address: {
                    name: shipToName,
                    address_line_1: addressLine1,
                    address_line_2: addressLine2,
                    city,
                    state,
                    zip_code: zipCode,
                    phone_number: phoneNumber,
                    email_address: emailAddress
                }
            })
            // For now, reload the page:
            window.location.reload()
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorCreatingLuluPrintJob(errorMessage)
            setCreatingLuluPrintJob(false)
        }
    }, [estimatedCost, axios, service_generated_book.unique_identifier, numberOfBooks, selectedShippingOption, shipToName, addressLine1, addressLine2, city, state, zipCode, phoneNumber, emailAddress])

    const onFormSubmitted = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        estimateCosts(null, null)
    }, [estimateCosts])

    const singleAddressLine = useMemo(() => {
        const components = [`${addressLine1}`, `${addressLine2}`, `${city}, ${state} ${zipCode}`].filter((component) => component.length > 0)
        return components.join(', ')
    }, [addressLine1, addressLine2, city, state, zipCode])

    if (estimatingCost) {
        return <div>
            <div className="flex justify-center items-center" >
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        </div>
    }

    if (estimatedCost) {
        return <div>
            <div className="cursor-pointer text-blue-500" onClick={() => {
                setEstimatedCost(null)
            }}>
                <h4>
                    {`Shipping to ${singleAddressLine}`}
                </h4>
            </div>
            {estimatedCost.line_item_costs.length === 1 ? <div className="flex align-center flex-row mt-4">
                <select value={numberOfBooks} onChange={(event) => {
                    setNumberOfBooks(event.target.value)
                    estimateCosts(parseInt(event.target.value, 10))
                }} className="border border-gray-300 rounded-md text-black">
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                </select>
                <div className="ml-2 mt-2">
                    {`book${numberOfBooks === '1' ? '' : 's'} - $${estimatedCost.line_item_costs[0].total_cost_incl_tax}`}
                </div>
            </div> : <div className="mt-4">
                {`Invalid number of line items`}
            </div>}
            {!loadingShippingOptions && <div className="mt-4">
                {errorLoadingShippingOptions ? <>
                    <p className="text-red-500">{errorLoadingShippingOptions}</p>
                </> : <>
                    <select
                        value={selectedShippingOption}
                        onChange={(event) => {
                            setSelectedShippingOption(event.target.value)
                            estimateCosts(null, event.target.value)
                        }}
                        className="border border-gray-300 rounded-md text-black"
                    >
                        {availableShippingOptions.map((option, index) => (
                            <option key={index} value={option.level}>
                                {option.level === selectedShippingOption ? <>
                                    {`$${estimatedCost.shipping_cost.total_cost_incl_tax} - ${option.carrier_service_name} (between ${DateTime.fromISO(option.min_delivery_date).toFormat('MMMM d')} and ${DateTime.fromISO(option.max_delivery_date).toFormat('MMMM d')})`}
                                </> : <>
                                    {`~$${option.cost_excl_tax} - ${option.carrier_service_name} (between ${DateTime.fromISO(option.min_delivery_date).toFormat('MMMM d')} and ${DateTime.fromISO(option.max_delivery_date).toFormat('MMMM d')})`}
                                </>}
                            </option>
                        ))}
                    </select>
                </>}
            </div>}
            <div className="mt-4">
                <div>
                    {`Fulfilment cost: $${estimatedCost.fulfillment_cost.total_cost_incl_tax}`}
                </div>
                <div>
                    ---
                </div>
                <div>
                    {`Total Cost: $${estimatedCost.total_cost_incl_tax}`}
                </div>
            </div>
            <div className="mt-4">
                <div className="flex justify-between">
                    <button
                        type="button"
                        className="btn btn-lg btn-light"
                        onClick={() => {
                            setEstimatedCost(null)
                        }}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        className="btn btn-lg btn-primary"
                        onClick={() => {
                            handleCreateLuluPrintJob()
                        }}
                        disabled={creatingLuluPrintJob}
                    >
                        {!creatingLuluPrintJob ? `Submit Order` : `Submitting Order...`}
                    </button>
                </div>
            </div>
            {errorCreatingLuluPrintJob && <p className="mt-4 text-red-500">{errorCreatingLuluPrintJob}</p>}
            {successCreatingLuluPrintJob && <p className="mt-4 text-green-500">{successCreatingLuluPrintJob}</p>}
        </div>
    }

    return <form onSubmit={onFormSubmitted}>
        <h2 className="text-lg font-bold">Shipping details</h2>
        <div className="mt-4">
            <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md text-black"
                placeholder="Name of recipient"
                required
                name="shipToName"
                value={shipToName}
                onChange={(event) => setShipToName(event.target.value)}
            />
        </div>
        <div className="mt-4">
            <div className="flex space-x-4">
                <div className="flex flex-1">
                    <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-md text-black"
                        placeholder="Address Line 1"
                        required
                        name="addressLine1"
                        value={addressLine1}
                        onChange={(event) => setAddressLine1(event.target.value)}
                    />
                </div>
                <div className="flex flex-1">
                    <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-md text-black"
                        placeholder="Address Line 2"
                        name="addressLine2"
                        value={addressLine2}
                        onChange={(event) => setAddressLine2(event.target.value)}
                    />
                </div>
            </div>
        </div>
        <div className="mt-4">
            <div className="flex space-x-4">
                <div className="flex flex-1">
                    <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-md text-black"
                        placeholder="City"
                        required
                        name="city"
                        value={city}
                        onChange={(event) => setCity(event.target.value)}
                    />
                </div>
                <div className="flex flex-1">
                    <select
                        className="w-full p-2 border border-gray-300 rounded-md text-black"
                        required
                        name="state"
                        value={state}
                        onChange={(event) => setState(event.target.value)}
                    >
                        <option value="">Select a state</option>
                        {states.map((state, index) => {
                            return <option key={index} value={state.value}>{state.label}</option>
                        })}
                    </select>
                </div>
                <div className="flex flex-1">
                    <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-md text-black"
                        placeholder="Zip Code"
                        required
                        name="zipCode"
                        value={zipCode}
                        onChange={(event) => setZipCode(event.target.value)}
                    />
                </div>
            </div>
        </div>
        <div className="mt-4">
            <div className="flex space-x-4">
                <div className="flex flex-1">
                    <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-md text-black"
                        placeholder="Phone number"
                        required
                        name="phoneNumber"
                        value={phoneNumber}
                        onChange={(event) => setPhoneNumber(event.target.value)}
                    />
                </div>
                <div className="flex flex-1">
                    <input
                        type="email"
                        className="w-full p-2 border border-gray-300 rounded-md text-black"
                        placeholder="Email address"
                        required
                        name="emailAddress"
                        value={emailAddress}
                        onChange={(event) => setEmailAddress(event.target.value)}
                    />
                </div>
            </div>
        </div>
        {errorMessageEstimatingCost && <p className="mt-4 text-red-500">{errorMessageEstimatingCost}</p>}
        <div className="mt-4">
            <div className="flex justify-between">
                <Link
                    to={`/books`}
                    className="btn btn-lg btn-light"
                >
                    Back
                </Link>
                <Button
                    color="blue"
                    className="cursor-pointer"
                    type="submit"
                    disabled={estimatingCost}
                >
                    Next
                </Button>
            </div>
        </div>
    </form>

}

export function OrderBookForm({
    book,
}: {
    book: PhysicalBook,
}) {
    return <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
        <div className="col-span-2">
            <div className="flex flex-col gap-5 lg:gap-7.5">
                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">
                            {`Order ${book.service.public_data.deceased_person.full_name}'s book`}
                        </h3>
                    </div>
                    <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                        {false && <>
                            {/*
                            {book && <>
                                {book.public_data.ipad_templates && <>
                                    <div>
                                        <img src={book.public_data.ipad_templates.portrait_url} alt="Portrait template" className="w-full h-auto" />
                                    </div>
                                </>}
                            </>}
                            */}
                        </>}
                        <div className="flex flex-col gap-4">
                            <div className="mb-4">
                                {book.public_data.output_urls ? <>
                                    <div>
                                        <h1 className="text-lg font-bold">
                                            {`Preview URLs:`}
                                        </h1>
                                        <ul>
                                            <li>
                                                <a
                                                    className="text-blue-500 cursor-pointer underline"
                                                    href={book.public_data.output_urls.final_url_for_cover} target="_blank" rel="noreferrer">
                                                    {`Cover page`}
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    className="text-blue-500 cursor-pointer underline"
                                                    href={book.public_data.output_urls.final_url_for_interior} target="_blank" rel="noreferrer">
                                                    {`Interior page`}
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </> : <>
                                    <div>
                                        <h1>
                                            {`No output urls found for service_generated_book with identifier ${book.unique_identifier}`}
                                        </h1>
                                    </div>
                                </>}
                            </div>
                            <div>
                                <ShippingPreview
                                    service_generated_book={book}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
}
